/**
 * Tenant Usage Page
 *
 * Route: /studio/admin/tenants/:tenantId/usage
 *
 * Displays usage metrics for a specific tenant:
 * - User count
 * - Vertical count
 * - Product count
 * - Recent audit activity (last 10 events)
 *
 * Gated to users with admin:tenants capability.
 */

import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  RefreshCw,
  Users,
  Layers,
  Package,
  Activity,
} from "lucide-react";
import type { ReactElement } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { apiRequest } from "../../../api/client";
import { MetricCard } from "../../../components/admin/MetricCard";
import { useAuthStore } from "../../../store/authStore";

// ============================================================================
// Types
// ============================================================================

interface RecentActivityEntry {
  readonly id: string;
  readonly action: string;
  readonly actor: string;
  readonly target: string;
  readonly createdAt: string;
}

interface TenantUsageData {
  readonly userCount: number;
  readonly verticalCount: number;
  readonly productCount: number;
  readonly recentActivity: readonly RecentActivityEntry[];
}

// ============================================================================
// Main Component
// ============================================================================

export function TenantUsagePage(): ReactElement {
  const { hasCapability } = useAuthStore();

  if (!hasCapability("admin:tenants")) {
    return <Navigate to="/studio" replace />;
  }

  return <TenantUsageContent />;
}

function TenantUsageContent(): ReactElement {
  const { tenantId } = useParams<{ tenantId: string }>();

  const {
    data: usage,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["tenant-usage", tenantId],
    queryFn: async () => {
      const response = await apiRequest<TenantUsageData>(`/api/v1/tenants/${tenantId}/usage`);
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: Boolean(tenantId),
  });

  if (isLoading) {
    return <LoadingState />;
  }

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Failed to load usage metrics"}
        onRetry={() => void refetch()}
      />
    );
  }

  if (!usage) {
    return <LoadingState />;
  }

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="mb-6">
        <Link
          to={`/studio/admin/tenants/${tenantId}`}
          className="inline-flex items-center gap-1 text-sm text-foreground-muted hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Tenant
        </Link>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Tenant Usage</h1>
            <p className="mt-1 font-mono text-sm text-foreground-muted">{tenantId}</p>
          </div>
          <button onClick={() => void refetch()} className="btn btn-secondary">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricCard
          label="Users"
          value={usage.userCount}
          icon={<Users className="h-4 w-4" />}
          description="Total users in this tenant"
        />
        <MetricCard
          label="Verticals"
          value={usage.verticalCount}
          icon={<Layers className="h-4 w-4" />}
          description="Configured verticals"
        />
        <MetricCard
          label="Products"
          value={usage.productCount}
          icon={<Package className="h-4 w-4" />}
          description="Active products (excluding deleted)"
        />
      </div>

      {/* Recent Activity */}
      <div className="card overflow-hidden">
        <div className="border-b border-border bg-background px-4 py-3 flex items-center gap-2">
          <Activity className="h-4 w-4 text-foreground-muted" />
          <h2 className="text-sm font-semibold">Recent Activity</h2>
          <span className="ml-auto text-xs text-foreground-muted">Last 10 events</span>
        </div>

        {usage.recentActivity.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
            <Activity className="h-8 w-8" />
            <p className="mt-2 text-sm">No activity recorded yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                    Actor
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                    Target
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted">
                    Timestamp
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {usage.recentActivity.map((entry) => (
                  <tr key={entry.id} className="hover:bg-background-secondary transition-colors">
                    <td className="px-4 py-3 text-sm font-mono font-medium">{entry.action}</td>
                    <td className="px-4 py-3 text-sm text-foreground-muted">{entry.actor}</td>
                    <td className="px-4 py-3 text-sm font-mono text-foreground-muted">
                      {entry.target || <span className="italic">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-muted whitespace-nowrap">
                      {new Date(entry.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// State Components
// ============================================================================

function LoadingState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
      <Loader2 className="h-8 w-8 animate-spin" />
      <p className="mt-4">Loading usage metrics...</p>
    </div>
  );
}

interface ErrorStateProps {
  readonly message: string;
  readonly onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
        <AlertCircle className="h-6 w-6 text-error" />
      </div>
      <p className="mt-4 text-foreground-muted">{message}</p>
      <button onClick={onRetry} className="btn btn-secondary mt-4">
        <RefreshCw className="mr-2 h-4 w-4" />
        Try again
      </button>
    </div>
  );
}
